import type { Translator } from '@webgame/i18n';
import { createContext, type ComponentChildren } from 'preact';
import { useContext } from 'preact/hooks';
import type { DevTools, GameClient } from '../core';
import type { SessionClient } from '../core/session/session-client';
import type { GameProbe } from '../game/game-probe';
import type { HintsSetting } from './settings/hints-setting';
import type { BloodSetting } from './settings/blood-setting';
import type { HitFeelSetting } from './settings/hit-feel-setting';
import type { SoundSetting } from './settings/sound-setting';
import type { AudioEngine } from '../audio/audio-engine';
import type { QuestGuideSetting } from './settings/quest-guide';

/**
 * What the page's first screens need (NET-CLIENT-CACHE C3): the session and the translator. Made
 * in main.tsx before anything of the game is downloaded.
 */
export interface BootServices {
  readonly session: SessionClient;
  readonly translator: Translator;
}

/** The game's long-lived services, made with the game's chunk once a character enters. */
export interface AppServices {
  readonly client: GameClient;
  readonly translator: Translator;
  /** Local dev panel tooling (F4); only answers on a dev server. */
  readonly devTools: DevTools;
  /** How much of a blow's feel this viewer wants (B022). */
  readonly hitFeel: HitFeelSetting;
  /** Whether this viewer sees blood (IU-2, on by default). */
  readonly blood: BloodSetting;
  /** How loud this viewer wants the game (ADR 0083). */
  readonly sound: SoundSetting;
  /** The game's sound: one per page, silent until a gesture (ADR 0083). */
  readonly audio: AudioEngine;
  /** The hints each character wants (CHAR-ORIGIN OG-4 a)): after a death, the first-time tips. */
  readonly hints: HintsSetting;
  /** The quest guide's pins and how much it marks (QUEST QG-2, QG-4). */
  readonly questGuide: QuestGuideSetting;
  /** What the scene drew, for tests: only in development and e2e builds. */
  readonly probe?: GameProbe;
}

const BootContext = createContext<BootServices | null>(null);
const ServicesContext = createContext<AppServices | null>(null);
const TranslatorContext = createContext<Translator | null>(null);

export function BootProvider(props: { services: BootServices; children: ComponentChildren }) {
  return (
    <BootContext.Provider value={props.services}>
      <TranslatorContext.Provider value={props.services.translator}>
        {props.children}
      </TranslatorContext.Provider>
    </BootContext.Provider>
  );
}

export function ServicesProvider(props: { services: AppServices; children: ComponentChildren }) {
  return (
    <ServicesContext.Provider value={props.services}>
      <TranslatorContext.Provider value={props.services.translator}>
        {props.children}
      </TranslatorContext.Provider>
    </ServicesContext.Provider>
  );
}

/** The session and the translator: the first screens' (sign-in, characters, the end). */
export function useBoot(): BootServices {
  const services = useContext(BootContext);
  if (!services) throw new Error('useBoot must be used inside <BootProvider>');
  return services;
}

export function useServices(): AppServices {
  const services = useContext(ServicesContext);
  if (!services) throw new Error('useServices must be used inside <ServicesProvider>');
  return services;
}

/** The translator, from whichever provider is above (the first screens' or the game's). */
export function useTranslatorService(): Translator {
  const translator = useContext(TranslatorContext);
  if (!translator) throw new Error('useTranslator must be used inside a services provider');
  return translator;
}
